// Pure logic for tracking "is a call active in some OTHER browser tab" via a shared, localStorage-
// backed set of busy tab ids. No DOM/storage access here — see activeCallGuard.ts for the actual
// localStorage + `storage`-event wiring, which is the ONLY caller of this file.
//
// ⛔ 29 Sep 2026: each of the three dialer surfaces (RtHubDialerProvider, SalesPowerDialerPage,
// VaDialerPage) builds its OWN independent Twilio Device, and Twilio's signaling server rings EVERY
// registered Device for an identity on an incoming call — per-Device, with no awareness of any
// sibling tab. A rep on a call in one tab, with the global RtHubDialerProvider also mounted in a
// second open tab (completely normal — it mounts on every page), still hears that second tab's own
// full-volume ringtone layer over the call they're actually on: that tab's own Device correctly has
// no active call, so its own busy-check never suppresses anything. Reported live: Anestasia, 29 Sep
// 2026 — "it rings over the call I'm already on... quite loud". See activeCallGuard.ts's own header
// for the rest of the story.

// ⛔ 13 Oct 2026 (audit): the busy set used to be a bare list of tab ids with no notion of time. A tab that
// died mid-call without running its cleanup (crash, force-quit, laptop out of battery — `pagehide` does not
// fire for any of those) left its id behind, and localStorage survives a browser restart, so the id lived
// FOREVER: from then on every tab on that machine believed "another tab is on a call", muted its ring, and
// held every inbound call in standby until the 3 s failsafe — a different tone, three seconds late, for every
// call, with nothing that ever cleared it. Each entry now carries the time its tab last said "still busy"; a
// tab on a call re-says it every BUSY_HEARTBEAT_MS, and an entry older than BUSY_TAB_TTL_MS is ignored.

/** A tab on a call refreshes its entry this often. */
export const BUSY_HEARTBEAT_MS = 15_000;

/** An entry not refreshed within this long is a dead tab, not a busy one. Several heartbeats of slack: a
 *  background tab's timers can be throttled to once a minute, though a tab holding a live call is normally
 *  exempt (WebRTC audio). A dead tab therefore mutes the others for at most this long, not forever. */
export const BUSY_TAB_TTL_MS = 90_000;

/** tab id -> the time (ms since epoch) that tab last reported itself busy. */
export type BusyTabs = ReadonlyMap<string, number>;

/** Parses the shared busy-tabs value. Never throws — a malformed or foreign value (including the old
 *  array-of-ids format, which has no timestamps and so cannot be trusted) is just "nobody busy", never a
 *  crash. */
export function parseBusyTabs(raw: string | null): BusyTabs {
  const out = new Map<string, number>();
  if (!raw) return out;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out;
    for (const [id, ts] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof ts === 'number' && Number.isFinite(ts)) out.set(id, ts);
    }
  } catch {
    /* foreign value — nobody busy */
  }
  return out;
}

export function serializeBusyTabs(tabs: BusyTabs): string {
  return JSON.stringify(Object.fromEntries(tabs));
}

/** Is an entry written at `ts` still believable at `now`? A timestamp from the FUTURE by more than the TTL
 *  (a clock that was changed) is not believable either — it would otherwise pin a tab busy for as long as
 *  the clock error. */
function isFresh(ts: number, now: number, ttlMs: number): boolean {
  return now - ts <= ttlMs && ts - now <= ttlMs;
}

/** Records `tabId` as busy as of `now`, dropping every entry that has gone stale so the stored value cleans
 *  itself up whenever any live tab writes. Never mutates its input. */
export function withBusyTab(tabs: BusyTabs, tabId: string, now: number, ttlMs: number = BUSY_TAB_TTL_MS): BusyTabs {
  const next = new Map<string, number>();
  for (const [id, ts] of tabs) if (id !== tabId && isFresh(ts, now, ttlMs)) next.set(id, ts);
  next.set(tabId, now);
  return next;
}

/** Removes `tabId` (and, as above, every stale entry). Never mutates its input. */
export function withIdleTab(tabs: BusyTabs, tabId: string, now: number, ttlMs: number = BUSY_TAB_TTL_MS): BusyTabs {
  const next = new Map<string, number>();
  for (const [id, ts] of tabs) if (id !== tabId && isFresh(ts, now, ttlMs)) next.set(id, ts);
  return next;
}

/** Whether some tab OTHER than `selfTabId` is currently — as of `now` — marked busy. A stale entry is a dead
 *  tab and does not count. The only question either dialer surface actually needs answered: a tab never
 *  mutes itself over its own call. */
export function isAnyOtherTabBusy(tabs: BusyTabs, selfTabId: string, now: number, ttlMs: number = BUSY_TAB_TTL_MS): boolean {
  for (const [id, ts] of tabs) {
    if (id !== selfTabId && isFresh(ts, now, ttlMs)) return true;
  }
  return false;
}

/** Parses the shared "a call was just answered on another tab" broadcast — see
 *  activeCallGuard.ts's onCallAnsweredElsewhere for the full story. Never throws: a malformed or
 *  foreign value (a stale write from a since-changed format, or storage shared with something
 *  unrelated) is just "no signal", never a crash. */
export function parseAnsweredCallSid(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const callSid = (parsed as { callSid?: unknown }).callSid;
    return typeof callSid === 'string' && callSid.length > 0 ? callSid : null;
  } catch {
    return null;
  }
}
